import React, { useState } from 'react'
import {
  Box,
  Button,
  TextField,
  Typography,
  Grid,
  Paper,
  CircularProgress,
  Alert,
  Slider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Chip,
  Stack,
  Switch,
  FormControlLabel
} from '@mui/material'
import DownloadIcon from '@mui/icons-material/Download'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate'
import DeleteIcon from '@mui/icons-material/Delete'
import ImageUploader from './ImageUploader'
import axios from 'axios'

interface ImageWithCaption {
  imageData: string
  file: File
  caption: string
}

type CombineMode = 'grid' | 'overlay'

const ImageCombiner: React.FC = () => {
  const [mode, setMode] = useState<CombineMode>('grid')
  const [images, setImages] = useState<ImageWithCaption[]>([])
  const [outputFormat, setOutputFormat] = useState<string>('PNG')
  const [outputWidth, setOutputWidth] = useState<string>('')
  const [outputHeight, setOutputHeight] = useState<string>('')
  const [layout, setLayout] = useState<string>('auto')
  const [spacing, setSpacing] = useState<number>(10)
  const [overlayOpacity, setOverlayOpacity] = useState<number>(60)
  const [overlayAsMask, setOverlayAsMask] = useState<boolean>(true)
  const [maskColor, setMaskColor] = useState<string>('#FF0000')
  const [combinedImage, setCombinedImage] = useState<string>('')
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string>('')
  const [uploadingIndex, setUploadingIndex] = useState<number | null>(null)

  const handleAddImage = () => {
    setUploadingIndex(images.length)
  }

  const setImageAtIndex = (index: number, imageData: string, file: File, caption: string) => {
    const next = [...images]
    next[index] = { imageData, file, caption }
    setImages(next)
    setCombinedImage('')
    setError('')
  }

  const handleImageUpload = (imageData: string, file: File) => {
    if (uploadingIndex !== null) {
      const newImage: ImageWithCaption = {
        imageData,
        file,
        caption: `Obraz ${uploadingIndex + 1}`
      }
      setImages([...images, newImage])
      setUploadingIndex(null)
      setError('')
    }
  }

  const handleCancelUpload = () => {
    setUploadingIndex(null)
  }

  const handleRemoveImage = (index: number) => {
    const newImages = images.filter((_, i) => i !== index)
    setImages(newImages)
    setCombinedImage('')
  }

  const handleCaptionChange = (index: number, caption: string) => {
    const newImages = [...images]
    newImages[index].caption = caption
    setImages(newImages)
  }

  const handleCombine = async () => {
    if (mode === 'overlay') {
      if (!images[0]?.imageData || !images[1]?.imageData) {
        setError('W trybie Overlay wymagane są 2 obrazy (tło + overlay/maska)')
        return
      }
    } else if (images.length === 0) {
      setError('Dodaj przynajmniej jeden obraz')
      return
    }

    setLoading(true)
    setError('')

    try {
      const token = localStorage.getItem('token')

      const imagesPayload = mode === 'overlay'
        ? [images[0].imageData, images[1].imageData]
        : images.map(img => img.imageData)

      const payload: any = {
        mode,
        images: imagesPayload,
        captions: mode === 'overlay' ? [] : images.map(img => img.caption),
        output_format: outputFormat
      }

      if (mode === 'grid') {
        payload.layout = layout
        payload.spacing = spacing
        if (outputWidth && parseInt(outputWidth) > 0) {
          payload.output_width = parseInt(outputWidth)
        }
        if (outputHeight && parseInt(outputHeight) > 0) {
          payload.output_height = parseInt(outputHeight)
        }
      } else {
        payload.overlay_opacity = overlayOpacity
        payload.overlay_as_mask = overlayAsMask
        payload.mask_color = maskColor
      }

      const response = await axios.post(
        '/api/photo-tools/combine',
        payload,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      )

      if (response.data.status === 'success') {
        const mimeType = outputFormat === 'PNG' ? 'image/png' : 'image/jpeg'
        setCombinedImage(`data:${mimeType};base64,${response.data.combined_image}`)
      } else {
        setError(response.data.error || 'Nie udało się połączyć obrazów')
      }
    } catch (err: any) {
      console.error('Combine error:', err)
      setError(err.response?.data?.error || 'Wystąpił błąd podczas łączenia obrazów')
    } finally {
      setLoading(false)
    }
  }

  const handleDownload = () => {
    if (!combinedImage) return

    const link = document.createElement('a')
    const extension = outputFormat === 'PNG' ? 'png' : 'jpg'
    link.download = `combined-image.${extension}`
    link.href = combinedImage
    link.click()
  }

  const handleReset = () => {
    setMode('grid')
    setImages([])
    setCombinedImage('')
    setError('')
    setOutputWidth('')
    setOutputHeight('')
    setLayout('auto')
    setSpacing(10)
    setOutputFormat('PNG')
    setOverlayOpacity(60)
    setOverlayAsMask(true)
    setMaskColor('#FF0000')
    setUploadingIndex(null)
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Image Upload Section */}
      {!combinedImage && (
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Dodaj obrazy
          </Typography>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Tryb</InputLabel>
                <Select
                  value={mode}
                  label="Tryb"
                  onChange={(e) => {
                    const nextMode = e.target.value as CombineMode
                    setMode(nextMode)
                    setCombinedImage('')
                    setError('')
                    setUploadingIndex(null)
                    if (nextMode === 'overlay') {
                      setImages((prev) => prev.slice(0, 2))
                    }
                  }}
                >
                  <MenuItem value="grid">Grid (Combine)</MenuItem>
                  <MenuItem value="overlay">Overlay (nakładanie)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {mode === 'grid' ? (
            <>
              {/* Uploaded Images List */}
              {images.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Grid container spacing={2}>
                    {images.map((img, index) => (
                      <Grid item xs={12} sm={6} md={4} key={index}>
                        <Paper elevation={1} sx={{ p: 2, position: 'relative' }}>
                          <Box
                            component="img"
                            src={img.imageData}
                            alt={`Image ${index + 1}`}
                            sx={{
                              width: '100%',
                              height: '150px',
                              objectFit: 'cover',
                              borderRadius: 1,
                              mb: 1
                            }}
                          />
                          <TextField
                            fullWidth
                            size="small"
                            label="Podpis"
                            value={img.caption}
                            onChange={(e) => handleCaptionChange(index, e.target.value)}
                            sx={{ mb: 1 }}
                          />
                          <Chip
                            label={`${index + 1}`}
                            size="small"
                            color="primary"
                            sx={{ position: 'absolute', top: 8, left: 8 }}
                          />
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleRemoveImage(index)}
                            sx={{ position: 'absolute', top: 8, right: 8 }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}

              {/* Add Image Button */}
              {uploadingIndex === null && (
                <Button
                  variant="outlined"
                  startIcon={<AddPhotoAlternateIcon />}
                  onClick={handleAddImage}
                  fullWidth
                >
                  Dodaj obraz
                </Button>
              )}

              {/* Image Uploader */}
              {uploadingIndex !== null && (
                <Box>
                  <ImageUploader
                    label={`Obraz ${uploadingIndex + 1}`}
                    onImageUpload={handleImageUpload}
                    currentImage=""
                  />
                  <Button
                    variant="text"
                    onClick={handleCancelUpload}
                    sx={{ mt: 1 }}
                  >
                    Anuluj
                  </Button>
                </Box>
              )}
            </>
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <ImageUploader
                  label="Tło (obraz bazowy)"
                  currentImage={images[0]?.imageData || ''}
                  onImageUpload={(imageData, file) => setImageAtIndex(0, imageData, file, 'Background')}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <ImageUploader
                  label="Overlay (np. maska z tool Mask)"
                  currentImage={images[1]?.imageData || ''}
                  disabled={!images[0]?.imageData}
                  onImageUpload={(imageData, file) => setImageAtIndex(1, imageData, file, 'Overlay')}
                />
              </Grid>
            </Grid>
          )}
        </Paper>
      )}

      {/* Settings Panel */}
      {images.length > 0 && !combinedImage && (
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Ustawienia
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Format</InputLabel>
                <Select
                  value={outputFormat}
                  label="Format"
                  onChange={(e) => setOutputFormat(e.target.value)}
                >
                  <MenuItem value="PNG">PNG</MenuItem>
                  <MenuItem value="JPEG">JPEG</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {mode === 'grid' ? (
              <>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Układ</InputLabel>
                    <Select
                      value={layout}
                      label="Układ"
                      onChange={(e) => setLayout(e.target.value)}
                    >
                      <MenuItem value="auto">Auto</MenuItem>
                      <MenuItem value="1x2">1 rząd, 2 kolumny</MenuItem>
                      <MenuItem value="1x3">1 rząd, 3 kolumny</MenuItem>
                      <MenuItem value="2x1">2 rzędy, 1 kolumna</MenuItem>
                      <MenuItem value="2x2">2 rzędy, 2 kolumny</MenuItem>
                      <MenuItem value="2x3">2 rzędy, 3 kolumny</MenuItem>
                      <MenuItem value="3x1">3 rzędy, 1 kolumna</MenuItem>
                      <MenuItem value="3x2">3 rzędy, 2 kolumny</MenuItem>
                      <MenuItem value="3x3">3 rzędy, 3 kolumny</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Odstęp (px)"
                    value={spacing}
                    onChange={(e) => setSpacing(parseInt(e.target.value) || 10)}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Szerokość"
                    placeholder="Auto"
                    value={outputWidth}
                    onChange={(e) => setOutputWidth(e.target.value)}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Wysokość"
                    placeholder="Auto"
                    value={outputHeight}
                    onChange={(e) => setOutputHeight(e.target.value)}
                  />
                </Grid>
              </>
            ) : (
              <>
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Przezroczystość overlay: {overlayOpacity}%
                  </Typography>
                  <Slider
                    value={overlayOpacity}
                    onChange={(_, v) => setOverlayOpacity(v as number)}
                    min={0}
                    max={100}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={overlayAsMask}
                        onChange={(e) => setOverlayAsMask(e.target.checked)}
                      />
                    }
                    label="Traktuj overlay jako maskę (B/W)"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Kolor maski (hex)"
                    value={maskColor}
                    onChange={(e) => setMaskColor(e.target.value)}
                    disabled={!overlayAsMask}
                    placeholder="#FF0000"
                  />
                </Grid>
              </>
            )}
          </Grid>

          <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              onClick={handleCombine}
              disabled={loading}
              fullWidth
            >
              {loading ? <CircularProgress size={24} /> : (mode === 'overlay' ? 'Nałóż overlay' : 'Połącz obrazy')}
            </Button>
            <Button
              variant="outlined"
              startIcon={<RestartAltIcon />}
              onClick={handleReset}
              disabled={loading}
            >
              Reset
            </Button>
          </Box>
        </Paper>
      )}

      {/* Result */}
      {combinedImage && (
        <Box>
          <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Połączony obraz
            </Typography>
            <Box
              component="img"
              src={combinedImage}
              alt="Combined"
              sx={{
                width: '100%',
                height: 'auto',
                maxHeight: '600px',
                objectFit: 'contain',
                border: '1px solid #ddd',
                borderRadius: 1
              }}
            />
          </Paper>

          <Stack direction="row" spacing={2} justifyContent="center">
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleDownload}
            >
              Pobierz
            </Button>
            <Button
              variant="outlined"
              startIcon={<RestartAltIcon />}
              onClick={handleReset}
            >
              Nowe połączenie
            </Button>
          </Stack>
        </Box>
      )}
    </Box>
  )
}

export default ImageCombiner
